import { useMemo } from 'react';

import { Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    ThemeProvider,
    Tooltip,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import { podeCancelar, podeEditar, podeIncluir } from '../../../../hooks/usePermisionMRT';
import tableThemeCustom from '../../../form/tableThemeCustom';
import RegraFiscalDialog from '../RegraFiscalDialog';

function simNao(value) {
    return value ? 'Sim' : 'Não';
}

function consumidorFinal(value) {
    if (value === null || value === undefined) {
        return 'Todos';
    }

    return value ? 'Sim' : 'Não';
}

function formatarData(value) {
    if (!value) return '';

    const partes = String(value).slice(0, 10).split('-');

    if (partes.length !== 3) {
        return value;
    }

    return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

export default function TabRegras({ regras = [], module }) {
    const { state, actions } = module;
    const { regraExcluir } = state;

    const { abrirNovaRegra, abrirEdicaoRegra, solicitarExclusao, cancelarExclusao, confirmarExclusao } = actions;

    const columns = useMemo(
        () => [
            {
                id: 'contexto',
                header: 'CONTEXTO',
                columns: [
                    {
                        id: 'operacao',
                        header: 'Operação',
                        accessorFn: (row) => row.Operacao?.sigla || row.Operacao?.descricao || 'Todas',
                    },
                    {
                        id: 'ufDestino',
                        header: 'UF',
                        accessorFn: (row) => row.UfDestino?.uf || row.UfDestino?.descricao || 'Todas',
                    },
                    {
                        accessorKey: 'tipoDestinatario',
                        header: 'Destinatário',
                    },
                    {
                        accessorKey: 'consumidorFinal',
                        header: 'Consumidor final',
                        Cell: ({ cell }) => consumidorFinal(cell.getValue()),
                    },
                    {
                        accessorKey: 'vigenciaInicio',
                        header: 'Início',
                        Cell: ({ cell }) => formatarData(cell.getValue()),
                    },
                    {
                        accessorKey: 'vigenciaFim',
                        header: 'Fim',
                        Cell: ({ cell }) => formatarData(cell.getValue()),
                    },
                ],
            },
            {
                id: 'tributacao',
                header: 'TRIBUTAÇÃO',
                columns: [
                    {
                        accessorKey: 'cstIcms',
                        header: 'CST',
                    },
                    {
                        accessorKey: 'csosn',
                        header: 'CSOSN',
                    },
                    {
                        accessorKey: 'aliquotaIcms',
                        header: 'ICMS %',
                    },
                    {
                        accessorKey: 'mvaIcmsSt',
                        header: 'MVA-ST %',
                    },
                    {
                        accessorKey: 'cstPis',
                        header: 'CST PIS',
                    },
                    {
                        accessorKey: 'cstCofins',
                        header: 'CST COFINS',
                    },
                    {
                        accessorKey: 'cstIbsCbs',
                        header: 'CST IBS/CBS',
                    },
                ],
            },
            {
                id: 'controle',
                header: 'CONTROLE',
                columns: [
                    {
                        accessorKey: 'integracaoAtivo',
                        header: 'Publicada',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'integracaoTodasEmpresas',
                        header: 'Todas empresas',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'revisaoPendente',
                        header: 'Revisão',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'versao',
                        header: 'Versão',
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativa',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();

    return (
        <>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    columns={columns}
                    data={Array.isArray(regras) ? regras : []}
                    getRowId={(row) => row.uuid}
                    localization={MRT_Localization_PT_BR}
                    enableRowActions
                    positionActionsColumn="first"
                    initialState={{
                        density: 'compact',
                        pagination: {
                            pageIndex: 0,
                            pageSize: 10,
                        },
                    }}
                    muiPaginationProps={{
                        rowsPerPageOptions: [5, 10, 20, 50],
                    }}
                    renderTopToolbarCustomActions={() => (
                        <Button
                            type="button"
                            color="secondary"
                            variant="contained"
                            disabled={!podeIncluir()}
                            onClick={abrirNovaRegra}
                        >
                            Nova regra
                        </Button>
                    )}
                    renderRowActions={({ row }) => (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                            }}
                        >
                            <Tooltip title="Editar regra">
                                <span>
                                    <IconButton
                                        type="button"
                                        color="primary"
                                        disabled={!podeEditar()}
                                        onClick={() => abrirEdicaoRegra(row.original)}
                                    >
                                        <Edit />
                                    </IconButton>
                                </span>
                            </Tooltip>

                            <Tooltip title="Excluir regra">
                                <span>
                                    <IconButton
                                        type="button"
                                        color="error"
                                        disabled={!podeCancelar()}
                                        onClick={() => solicitarExclusao(row.original)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                />
            </ThemeProvider>

            <RegraFiscalDialog module={module} />

            <Dialog open={Boolean(regraExcluir)} onClose={cancelarExclusao}>
                <DialogTitle>Excluir regra fiscal?</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        A regra selecionada será excluída. A exclusão será bloqueada caso ela seja a última regra publicada de um
                        perfil que continua publicado.
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button type="button" onClick={cancelarExclusao}>
                        Cancelar
                    </Button>

                    <Button type="button" variant="contained" color="error" onClick={confirmarExclusao}>
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
